/**
 * OnboardingWizard.js — Post-persona "canvas onboarding" entry banner.
 *
 * Rather than walking the user through a multi-step wizard, this renders a slim,
 * single banner that points new users at the /flow pipeline page, which doubles
 * as the setup canvas. Setup itself happens on /flow (per-stage readiness +
 * Connect affordances). This banner only decides whether to invite the user
 * there and lets them dismiss setup.
 *
 * Visibility + dismissal are driven by the server-side onboarding status
 * (/api/onboarding/{status,dismiss,complete}) so reload / multi-device behaves
 * correctly. We no longer fire per-step advances — there are no steps here.
 *
 * F07: dismissing is NOT completing. "Skip for now" and the × record a
 * dismissal; only actually finishing setup on /flow records completion. The
 * banner collapses to a persistent "Finish setting up" link rather than
 * disappearing, so skipping never strands the user.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { useOnboardingStatus, qk } from '../lib/queries';
import Icon from './Icon';
import '../styles/entry-flow.css';

const SESSION_KEY = 'flow.onboarding.dismissedThisSession';

export default function OnboardingWizard() {
  const { data: status, isLoading } = useOnboardingStatus();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Once-per-session guard. Without this the banner would re-appear every
  // time the user re-renders the host page (e.g. after closing a modal),
  // which feels nagging. sessionStorage clears on tab close / logout.
  const [dismissedThisSession, setDismissedThisSession] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
  });

  // The user has implicitly "seen" the banner once it renders. Mark the
  // session flag after first render so navigating away then back doesn't
  // re-surface it.
  useEffect(() => {
    if (isLoading) return;
    if (!status || status.complete || !status.use_case) return;
    if (dismissedThisSession) return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {}
  }, [isLoading, status, dismissedThisSession]);

  if (isLoading) return null;
  if (!status) return null;
  if (status.complete) return null;          // setup genuinely finished
  if (!status.use_case) return null;         // persona modal will surface first

  const isDismissed = Boolean(status.dismissed) || dismissedThisSession;

  // F07 — Skip / × put the banner away. They record a dismissal, never a
  // completion, so an unfinished setup is never reported as done.
  const dismiss = async () => {
    setDismissedThisSession(true);
    // The focused control is about to disappear; move focus to the page
    // heading so keyboard users keep their place.
    setTimeout(() => {
      const h = document.querySelector('.main-content h1');
      if (!h) return;
      if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1');
      h.focus();
    }, 0);
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch {}
    try {
      await api.post('/onboarding/dismiss', { dismissed: true });
      queryClient.invalidateQueries({ queryKey: qk.onboardingStatus() });
    } catch { /* dismissal still applies locally */ }
  };

  const resume = async () => {
    setDismissedThisSession(false);
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    try {
      await api.post('/onboarding/dismiss', { dismissed: false });
      queryClient.invalidateQueries({ queryKey: qk.onboardingStatus() });
    } catch { /* the expanded banner still shows locally */ }
  };

  // F07 — dismissed collapses to a persistent, resumable entry instead of
  // vanishing. Setup is still unfinished and the user can still get back to it.
  if (isDismissed) {
    return (
      <p className="en-banner-resume">
        <Icon name="flow" size={16} aria-hidden="true" />
        <span>Your Flow pipeline isn't set up yet.</span>
        <button type="button" className="btn btn-link btn-sm" onClick={resume}>
          Finish setting up
        </button>
      </p>
    );
  }

  return (
    <section className="en-banner" aria-labelledby="en-banner-title">
      <span className="en-banner-icon" aria-hidden="true"><Icon name="flow" size={22} /></span>
      <div className="en-banner-text">
        <p id="en-banner-title" className="en-banner-title">Set up your Flow pipeline</p>
        <p className="en-banner-body">
          Choose how work moves from design to support and connect your tools. It takes a minute.
        </p>
      </div>
      <div className="en-banner-actions">
        <button type="button" className="btn btn-primary" onClick={() => navigate('/flow')}>
          Set up your pipeline
        </button>
        <button type="button" className="btn btn-ghost" onClick={dismiss}>
          Skip for now
        </button>
      </div>
      <button
        type="button"
        className="icon-btn en-banner-close"
        onClick={dismiss}
        aria-label="Dismiss setup banner"
      >
        <Icon name="close" />
      </button>
    </section>
  );
}
